import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Filter, X, BarChart2, AlertCircle, BookOpen, Award, Hash, PieChart, Activity, Grid } from 'lucide-react';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { useActiveCourse } from '../context/ActiveCourseContext';
import { questionAPI } from '../utils/api';
import { getAvailableSets, getAvailableSetTypes, matchesFilters } from '../utils/filterUtils';
import { getMarksDistribution, getAverageMarksPerChapter, getSubtopicHeatmapData } from '../utils/analyticsUtils';
import SetSelectionDropdown from '../components/SetSelectionDropdown';
import FilterSourcePill from '../components/FilterSourcePill';
import { BarChart, Bar, PieChart as RePieChart, Pie, Cell, Sector, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const Filtered = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { activeCourseId, activeCourseData } = useActiveCourse();

  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [displayedQuestions, setDisplayedQuestions] = useState([]);
  const [questionsToShow, setQuestionsToShow] = useState(20);
  const [loading, setLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [activePieIndex, setActivePieIndex] = useState(null);

  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('questionFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if it matches the current course (if activeCourseId is available)
        // If activeCourseId is null/undefined initially, we might default, 
        // but the useEffect below will handle the sync once activeCourseId is ready.
        if (parsed.courseId === activeCourseId) {
          return parsed.filters;
        }
      }
    } catch (e) {
      console.error('Failed to parse filters', e);
    }

    return {
      marksRange: [0, 20],
      setTypes: { R: false, B: false, RB: false },
      selectedSets: [],
      tags: [],
      chapters: [],
      filterSources: {
        subtype: false,
        syllabus: false
      }
    };
  });

  // Handle activeCourseId changes: Load saved filters for this course OR reset to defaults
  useEffect(() => {
    if (!activeCourseId) return;

    try {
      const saved = localStorage.getItem('questionFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.courseId === activeCourseId) {
          setFilters(parsed.filters);
          return;
        }
      }
    } catch (e) {
      console.error('Failed to load filters', e);
    }

    // If no saved filters for this course, reset to defaults
    setFilters({
      marksRange: [0, 20], // Will be updated by marksBounds
      setTypes: { R: false, B: false, RB: false },
      selectedSets: [],
      tags: [],
      chapters: [],
      filterSources: { subtype: false, syllabus: false }
    });
  }, [activeCourseId]);

  // Persist filters to localStorage with courseId
  useEffect(() => {
    if (activeCourseId) {
      localStorage.setItem('questionFilters', JSON.stringify({
        courseId: activeCourseId,
        filters
      }));
    }
  }, [filters, activeCourseId]);

  useEffect(() => {
    setDemoMode(!isAuthenticated);
  }, [isAuthenticated]);

  // Auto-load questions on mount or from navigation
  useEffect(() => {
    const state = location.state;

    // If coming from another page with specific filters, apply them
    if (state) {
      const newFilters = { ...filters };
      let shouldApply = false;

      if (state.filterType === 'tag' && state.filterValue) {
        newFilters.tags = [state.filterValue];
        newFilters.filterSources.subtype = true;
        shouldApply = true;
      }

      if (state.filterSubtopic) {
        newFilters.chapters = [state.filterSubtopic];
        newFilters.filterSources.syllabus = true;
        shouldApply = true;
      }

      if (shouldApply) {
        setFilters(newFilters);
      }
    }

    // Always auto-load questions if not loaded
    if (activeCourseId && allQuestions.length === 0) {
      fetchAllQuestions(false);
    }
  }, [location.state, activeCourseId]);

  const fetchAllQuestions = async (showToast = true) => {
    if (!activeCourseId) {
      toast.error('Please activate a course first');
      return;
    }

    setLoading(true);
    try {
      // Fetch all questions with a high limit to ensure client-side filtering works on the full dataset
      const response = await questionAPI.filterQuestions(activeCourseId, { limit: 10000 });
      setAllQuestions(response.data.data || []);
      if (showToast) {
        toast.success(`Loaded ${response.data.data?.length || 0} questions`);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      console.error('Failed to load questions');
      setAllQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const availableSets = useMemo(() => getAvailableSets(allQuestions), [allQuestions]);
  const availableSetTypes = useMemo(() => getAvailableSetTypes(allQuestions), [allQuestions]);

  const availableTags = useMemo(() => {
    const tagSet = new Set();
    allQuestions.forEach(q => {
      if (q.tags && Array.isArray(q.tags)) {
        q.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [allQuestions]);

  const availableChapters = useMemo(() => {
    const chapterSet = new Set();
    const subtopicSet = new Set();

    allQuestions.forEach(q => {
      if (q.chSubtopic && Array.isArray(q.chSubtopic)) {
        q.chSubtopic.forEach(subtopic => {
          // Add exact subtopic
          subtopicSet.add(subtopic);

          // Extract chapter number (e.g., "1" from "1.1", "1.2")
          const chapterNum = subtopic.split('.')[0];
          if (chapterNum) {
            chapterSet.add(`Chapter ${chapterNum}`);
          }
        });
      }
    });

    // Combine chapters and subtopics, with chapters first
    const chapters = Array.from(chapterSet).sort((a, b) => {
      const numA = parseInt(a.replace('Chapter ', ''));
      const numB = parseInt(b.replace('Chapter ', ''));
      return numA - numB;
    });

    const subtopics = Array.from(subtopicSet).sort((a, b) => {
      const [chA, subA] = a.split('.').map(Number);
      const [chB, subB] = b.split('.').map(Number);
      return chA === chB ? subA - subB : chA - chB;
    });

    return [...chapters, ...subtopics];
  }, [allQuestions]);

  const marksBounds = useMemo(() => {
    if (allQuestions.length === 0) return [0, 20];
    const marks = allQuestions.map(q => parseFloat(q.marks) || 0);
    const min = Math.floor(Math.min(...marks));
    const max = Math.ceil(Math.max(...marks));
    return [min, max];
  }, [allQuestions]);

  // Update marks range when questions load
  useEffect(() => {
    if (allQuestions.length > 0) {
      setFilters(prev => {
        // Only update if current range is default [0, 20]
        if (prev.marksRange[0] === 0 && prev.marksRange[1] === 20) {
          return { ...prev, marksRange: marksBounds };
        }
        return prev;
      });
    }
  }, [marksBounds, allQuestions.length]);

  // Calculate frequency for each question (how many times it appears)
  const questionFrequencyMap = useMemo(() => {
    const frequencyMap = new Map();

    // Group questions by rootQuestion
    const rootQuestionMap = new Map();
    allQuestions.forEach(q => {
      const root = q.rootQuestion || q.uniqueId;
      if (!rootQuestionMap.has(root)) {
        rootQuestionMap.set(root, []);
      }
      rootQuestionMap.get(root).push(q);
    });

    // Create frequency map for each question
    allQuestions.forEach(q => {
      const root = q.rootQuestion || q.uniqueId;
      const frequency = rootQuestionMap.get(root)?.length || 1;
      frequencyMap.set(q._id, frequency);
    });

    return frequencyMap;
  }, [allQuestions]);

  useEffect(() => {
    const filtered = allQuestions.filter(q => matchesFilters(q, filters));
    const sorted = [...filtered].sort((a, b) => {
      // Extract year and question number from uniqueId (format: YEAR_MONTH_SETTYPE_QNO)
      const partsA = a.uniqueId?.split('_') || [];
      const partsB = b.uniqueId?.split('_') || [];

      const yearA = parseInt(partsA[0]) || 0;
      const yearB = parseInt(partsB[0]) || 0;

      // First sort by year (newest first)
      if (yearB !== yearA) {
        return yearB - yearA;
      }

      // If same year, sort by question number (ascending: 1, 2, 3...)
      // Extract number from 'Q9', 'Q2', etc. by removing the 'Q' prefix
      const questionNoA = parseInt(partsA[3]?.replace(/Q/i, '')) || 0;
      const questionNoB = parseInt(partsB[3]?.replace(/Q/i, '')) || 0;

      return questionNoA - questionNoB;
    });
    setFilteredQuestions(sorted);
    setQuestionsToShow(20);
  }, [allQuestions, filters]);

  useEffect(() => {
    setDisplayedQuestions(filteredQuestions.slice(0, questionsToShow));
  }, [filteredQuestions, questionsToShow]);

  const averageMarksData = useMemo(() => getAverageMarksPerChapter(filteredQuestions), [filteredQuestions]);
  const totalAverageMarks = useMemo(() => averageMarksData.reduce((sum, item) => sum + item.avgMarks, 0), [averageMarksData]);

  const applyFilters = () => {
    // If no questions loaded, fetch all questions regardless of filters
    if (allQuestions.length === 0) {
      fetchAllQuestions(true);
    } else {
      toast.success(`Showing ${filteredQuestions.length} question${filteredQuestions.length !== 1 ? 's' : ''}`);
    }
    if (window.innerWidth < 768) setShowFilters(false);
  };

  const resetFilters = () => {
    const defaultFilters = {
      marksRange: marksBounds,
      setTypes: { R: false, B: false, RB: false },
      selectedSets: [],
      tags: [],
      chapters: [],
      filterSources: { subtype: false, syllabus: false }
    };
    setFilters(defaultFilters);
    localStorage.removeItem('questionFilters');
    setQuestionsToShow(20);
    if (allQuestions.length === 0) {
      fetchAllQuestions(false);
    }
    toast.success('Filters reset');
  };

  const loadMoreQuestions = () => {
    setQuestionsToShow(prev => prev + 20);
  };

  const clearSubtypeFilter = () => {
    setFilters(prev => ({
      ...prev,
      tags: [],
      filterSources: { ...prev.filterSources, subtype: false }
    }));
  };

  const clearSyllabusFilter = () => {
    setFilters(prev => ({
      ...prev,
      chapters: [],
      filterSources: { ...prev.filterSources, syllabus: false }
    }));
  };

  const handleQuestionClick = (question) => {
    // Navigate to MasterType page with the question's uniqueId
    navigate('/master', {
      state: {
        selectedQuestionId: question.uniqueId,
        fromFiltered: true
      }
    });
  };


  if (!activeCourseId) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-400">
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Course</h2>
            <p className="text-gray-600 max-w-md mx-auto">Please activate a course from dashboard.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                Advanced Filtering
                {demoMode && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
                    Demo Mode
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-2">
                Find specific questions by marks, set type, and more.
              </p>
            </div>

            {activeCourseData && (
              <div className="bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-400 flex items-center gap-3">
                <div>
                  <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Active Course</div>
                  <div className="font-bold text-gray-900">{activeCourseData.name}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Filters */}
          <aside className="lg:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-24 bg-white rounded-xl shadow-sm border border-gray-400">
              <div
                className="px-4 py-3 border-b border-gray-300 flex items-center justify-between lg:cursor-default cursor-pointer hover:bg-gray-50/50 lg:hover:bg-transparent transition-colors"
                onClick={(e) => {
                  if (window.innerWidth < 1024 && !e.target.closest('button')) {
                    setShowFilters(!showFilters);
                  }
                }}
              >
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-blue-600" />
                  Filters
                </h2>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetFilters();
                  }}
                  className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-400 rounded-lg font-semibold text-xs hover:bg-gray-100 transition-colors flex items-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>

              <div className={`${showFilters ? 'block' : 'hidden'} lg:block p-4`}>
                {/* Filter Controls */}
                <div className="space-y-3">
                  {/* Sub-Type and Syllabus Filters - Side by Side */}
                  <div className="flex gap-2">
                    <FilterSourcePill
                      type="subtype"
                      value={filters.tags}
                      active={filters.filterSources.subtype}
                      onClear={clearSubtypeFilter}
                      interactive={true}
                      availableOptions={availableTags}
                      onSelectionChange={(newTags) => setFilters(prev => ({ ...prev, tags: newTags, filterSources: { ...prev.filterSources, subtype: newTags.length > 0 } }))}
                    />
                    <FilterSourcePill
                      type="syllabus"
                      value={filters.chapters}
                      active={filters.filterSources.syllabus}
                      onClear={clearSyllabusFilter}
                      interactive={true}
                      availableOptions={availableChapters}
                      onSelectionChange={(newChapters) => setFilters(prev => ({ ...prev, chapters: newChapters, filterSources: { ...prev.filterSources, syllabus: newChapters.length > 0 } }))}
                    />
                  </div>

                  {/* Marks Range */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-700 min-w-[80px]">
                      Marks Range
                    </label>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="number"
                        value={filters.marksRange[0]}
                        onChange={(e) => setFilters(prev => ({ ...prev, marksRange: [Number(e.target.value), prev.marksRange[1]] }))}
                        className="w-20 px-2 py-1.5 bg-gray-50 border border-gray-400 rounded-lg text-sm text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="Min"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <input
                        type="number"
                        value={filters.marksRange[1]}
                        onChange={(e) => setFilters(prev => ({ ...prev, marksRange: [prev.marksRange[0], Number(e.target.value)] }))}
                        className="w-20 px-2 py-1.5 bg-gray-50 border border-gray-400 rounded-lg text-sm text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="Max"
                      />
                    </div>
                  </div>

                  {/* Set Types */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-700 min-w-[80px]">
                      Set Type
                    </label>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.setTypes.R || false}
                          onChange={() => setFilters(prev => ({
                            ...prev,
                            setTypes: { ...prev.setTypes, R: !prev.setTypes.R },
                            selectedSets: []
                          }))}
                          className="w-4 h-4 text-blue-600 border-gray-400 rounded focus:ring-1 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Regular</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.setTypes.B || false}
                          onChange={() => setFilters(prev => ({
                            ...prev,
                            setTypes: { ...prev.setTypes, B: !prev.setTypes.B },
                            selectedSets: []
                          }))}
                          className="w-4 h-4 text-blue-600 border-gray-400 rounded focus:ring-1 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Back</span>
                      </label>
                      {availableSetTypes.RB && (
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.setTypes.RB || false}
                            onChange={() => setFilters(prev => ({
                              ...prev,
                              setTypes: { ...prev.setTypes, RB: !prev.setTypes.RB },
                              selectedSets: []
                            }))}
                            className="w-4 h-4 text-blue-600 border-gray-400 rounded focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">RB</span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Set Selection */}
                  <div>
                    <SetSelectionDropdown
                      availableSets={availableSets}
                      selectedSets={filters.selectedSets}
                      onChange={(newSets) => setFilters(prev => ({ ...prev, selectedSets: newSets }))}
                      selectedSetTypes={filters.setTypes}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 pt-4 mt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowAnalytics(!showAnalytics)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center gap-1.5"
                  >
                    <BarChart2 className="w-4 h-4" />
                    {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                  </button>
                </div>
              </div>
            </div>
          </aside>
          {/* Right Content - Analytics OR Results */}
          <div className="flex-1 min-w-0 overflow-auto max-h-[calc(100vh-140px)] filtered-results-scrollbar">
            {/* Analytics Dashboard */}
            {showAnalytics && (
              <div className="space-y-6 animate-fadeIn">
                {/* Header */}
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                    <BarChart2 className="w-8 h-8 text-blue-600" />
                    Analytics Dashboard
                  </h2>
                  <p className="text-gray-600">Comprehensive insights from {filteredQuestions.length} questions</p>
                </div>

                {/* Charts Grid - Each chart in its own row */}
                <div className="space-y-6">
                  {/* Subtopic Heatmap - FIRST ROW */}
                  {getSubtopicHeatmapData(filteredQuestions).length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-400">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Grid className="w-5 h-5 text-purple-600" />
                        Subtopic Question Heatmap
                      </h3>
                      <p className="text-gray-600">One Whole Questions is Assigned to Sub-Topic Carrying Highest Marks.</p>
                      <ResponsiveContainer width="100%" height={Math.max(300, Math.min(400, getSubtopicHeatmapData(filteredQuestions).length * 25 + 150))}>
                        <BarChart
                          data={getSubtopicHeatmapData(filteredQuestions)}
                          margin={{ top: 70, right: 10, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <filter id="heatGlow">
                              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                              <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                              </feMerge>
                            </filter>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                            vertical={false}
                            strokeOpacity={0.4}
                          />
                          <XAxis
                            dataKey="subtopic"
                            tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            axisLine={{ stroke: '#d1d5db', strokeWidth: 1.5 }}
                            tickLine={{ stroke: '#d1d5db' }}
                          />
                          <YAxis
                            tick={{ fontSize: 13, fill: '#6b7280', fontWeight: 500 }}
                            axisLine={{ stroke: '#d1d5db', strokeWidth: 1.5 }}
                            tickLine={{ stroke: '#d1d5db' }}
                            label={{
                              value: 'Total Marks',
                              angle: -90,
                              position: 'insideLeft',
                              style: { fill: '#374151', fontWeight: 600, fontSize: 14 }
                            }}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(139, 92, 246, 0.1)', radius: 10 }}
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                              padding: '12px 16px',
                              backgroundColor: 'rgba(255, 255, 255, 0.98)'
                            }}
                            labelStyle={{
                              fontWeight: 600,
                              color: '#8B5CF6',
                              fontSize: '13px',
                              marginBottom: '4px'
                            }}
                            formatter={(value, name, props) => {
                              const count = props.payload.count;
                              const totalMarks = props.payload.totalMarks;
                              return [`${count} questions, ${totalMarks} total marks`, ''];
                            }}
                          />
                          <Bar
                            dataKey="totalMarks"
                            radius={[10, 10, 0, 0]}
                            animationDuration={1000}
                            animationBegin={100}
                            filter="url(#heatGlow)"
                          >
                            {getSubtopicHeatmapData(filteredQuestions).map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={`rgba(139, 92, 246, ${0.3 + (entry.intensity / 100) * 0.7})`}
                                style={{
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.filter = 'brightness(1.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.filter = 'brightness(1)';
                                }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}



                  {/* Marks Distribution Pie Chart - THIRD ROW */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-400">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-blue-600" />
                      Marks Distribution
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <RePieChart>
                        <Pie
                          data={getMarksDistribution(filteredQuestions)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ marks, count }) => `${marks}: ${count}`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="count"
                          animationDuration={800}
                          activeIndex={activePieIndex}
                          activeShape={(props) => {
                            const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
                            return (
                              <g>
                                <defs>
                                  <filter id="glow">
                                    <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                                    <feMerge>
                                      <feMergeNode in="coloredBlur" />
                                      <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                  </filter>
                                </defs>
                                <Sector
                                  cx={cx}
                                  cy={cy}
                                  innerRadius={innerRadius}
                                  outerRadius={outerRadius + 8}
                                  startAngle={startAngle}
                                  endAngle={endAngle}
                                  fill={fill}
                                  stroke="#fff"
                                  strokeWidth={3}
                                  filter="url(#glow)"
                                  style={{
                                    filter: `drop-shadow(0 0 12px ${fill})`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                  }}
                                />
                              </g>
                            );
                          }}
                          onMouseEnter={(_, index) => setActivePieIndex(index)}
                          onMouseLeave={() => setActivePieIndex(null)}
                        >
                          {getMarksDistribution(filteredQuestions).map((entry, index) => {
                            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
                            return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                          })}
                        </Pie>
                        <Tooltip
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                          formatter={(value, name, props) => [`${value} questions (${props.payload.marks} marks)`, 'Count']}
                        />
                        <Legend />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Results - Only show when analytics is hidden */}
            {!showAnalytics && displayedQuestions.length > 0 ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    Results
                    <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                      {filteredQuestions.length}
                    </span>
                    {displayedQuestions.length < filteredQuestions.length && (
                      <span className="text-sm font-normal text-gray-500">
                        (showing {displayedQuestions.length})
                      </span>
                    )}
                  </h2>
                </div>

                <div className="grid gap-4">
                  {displayedQuestions.map((question, index) => (
                    <div
                      key={question._id}
                      onClick={() => handleQuestionClick(question)}
                      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-400 hover:shadow-md hover:border-blue-300 transition-all duration-300 group cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 font-bold text-sm border border-gray-200 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                          #{index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Header - Title and Sub-topic */}
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <h3 className="text-blue-600 font-bold text-lg tracking-tight font-mono">
                              {question.uniqueId}
                            </h3>

                            {/* Syllabus Sub-topics (chSubtopic) - Header Right */}
                            {question.chSubtopic && (
                              <div className="flex flex-wrap gap-1.5 justify-end">
                                {(Array.isArray(question.chSubtopic) ? question.chSubtopic : [question.chSubtopic]).map((subtopic, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-semibold border border-indigo-100">
                                    <BookOpen className="w-3 h-3" /> {subtopic}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Question Text */}
                          <p className="text-gray-800 mb-3 leading-relaxed font-medium group-hover:text-blue-900 transition-colors">
                            {question.cleanText}
                          </p>

                          {/* Bottom Section - Left: Marks & Frequency, Right: Sub-type tags */}
                          <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                            {/* Left Side - Marks and Frequency stacked */}
                            <div className="flex flex-col gap-2">
                              {/* Marks */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600">Marks:</span>
                                <span className="text-base font-bold text-gray-600">
                                  {question.marksPattern || question.marks}
                                </span>
                              </div>

                              {/* Frequency */}
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600">Frequency:</span>
                                <span className="text-base font-bold text-red-600">
                                  {questionFrequencyMap.get(question._id) || 1}
                                </span>
                              </div>
                            </div>

                            {/* Right Side - Sub-type tags */}
                            {question.tags && question.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 sm:justify-end sm:items-end">
                                {question.tags.map((tag, i) => (
                                  <span key={i} className="px-2.5 py-1 bg-gray-50 border border-gray-400 text-gray-600 rounded-lg text-xs font-medium">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More */}
                {
                  displayedQuestions.length < filteredQuestions.length && (
                    <div className="flex justify-center pt-6">
                      <button
                        onClick={loadMoreQuestions}
                        className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg flex items-center gap-2"
                      >
                        Load More
                        <span className="px-2 py-0.5 bg-blue-500 rounded-full text-xs">
                          +20
                        </span>
                      </button>
                    </div>
                  )
                }
              </div>
            ) : !showAnalytics ? (
              !loading && allQuestions.length > 0 && (
                <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-400">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Questions Found</h3>
                  <p className="text-gray-600 max-w-md mx-auto">Try adjusting your filters to find what you're looking for.</p>
                </div>
              )
            ) : null
            }

            {
              !showAnalytics && !loading && allQuestions.length === 0 && (
                <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-400">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="w-10 h-10 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Filter</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-4">Click "Apply" to load questions with your selected criteria.</p>
                  <button
                    onClick={() => fetchAllQuestions(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Load Questions
                  </button>
                </div>
              )
            }
          </div >
        </div >
      </main >
    </div >
  );
};

export default Filtered;
